import { useState, useEffect, useMemo } from 'react';
import { billingAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Receipt, MagnifyingGlass, SpinnerGap } from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  paid: 'bg-green-100 text-green-800',
  partially_paid: 'bg-amber-100 text-amber-800',
  partial: 'bg-amber-100 text-amber-800',
  sent: 'bg-blue-100 text-blue-800',
  overdue: 'bg-red-100 text-red-800',
  draft: 'bg-slate-100 text-slate-700',
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const Totals = ({ invoices }) => {
  const billed = invoices.reduce((s, i) => s + (i.subtotal || 0), 0);
  const paid = invoices.reduce((s, i) => s + (i.amount_paid || 0), 0);
  const due = invoices.reduce((s, i) => s + (i.balance_due || 0), 0);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {[['Total billed', billed, 'invoices-total-billed'], ['Collected', paid, 'invoices-total-paid'], ['Outstanding', due, 'invoices-total-due']].map(([label, value, id]) => (
        <Card key={label}><CardContent className="py-4">
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-900" data-testid={id}>{money(value)}</p>
        </CardContent></Card>
      ))}
    </div>
  );
};

export default function AdminInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    billingAPI.getInvoices()
      .then((res) => setInvoices(res.data || []))
      .catch(() => toast.error('Failed to load invoices'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return invoices.filter(i => !q || (i.patient_name || '').toLowerCase().includes(q) || (i.id || '').includes(q));
  }, [invoices, search]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-invoices-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-teal-100 rounded-lg"><Receipt size={28} className="text-teal-600" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Invoices</h1>
          <p className="text-slate-500 text-sm">All patient invoices, payments and balances</p>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><SpinnerGap size={32} className="animate-spin text-teal-600" /></div>
      ) : (
        <>
          <Totals invoices={invoices} />
          <div className="relative mb-4 max-w-sm">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search by patient" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="invoices-search-input" />
          </div>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Patient</th>
                    <th className="text-right p-3">Billed</th>
                    <th className="text-right p-3">Paid</th>
                    <th className="text-right p-3">Balance</th>
                    <th className="text-left p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-slate-500" data-testid="invoices-empty">No invoices yet.</td></tr>
                  )}
                  {filtered.map(inv => (
                    <tr key={inv.id} className="border-t border-slate-100" data-testid={`invoice-row-${inv.id}`}>
                      <td className="p-3">{new Date(inv.created_at).toLocaleDateString()}</td>
                      <td className="p-3 font-medium">{inv.patient_name}</td>
                      <td className="p-3 text-right">{money(inv.subtotal)}</td>
                      <td className="p-3 text-right">{money(inv.amount_paid)}</td>
                      <td className="p-3 text-right">{money(inv.balance_due)}</td>
                      <td className="p-3"><Badge className={STATUS_STYLES[inv.status] || STATUS_STYLES.draft}>{(inv.status || 'draft').replace(/_/g, ' ')}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
